import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { environment } from "../../../environments/environment";
import { GenericFilterRequestDto } from "../models/generic-filter-request.dto";
import { GenericSortRequestDto } from "../models/generic-sort-request.dto";
import { GenericResponseDto } from "../models/generic-response.dto";
import { ClientGroupDetailDto } from "../models/client-group/client-group-detail.dto";
import { ClientGroupSummaryDto } from "../models/client-group/client-group-summary.dto";
import { ClientUserDto } from "../models/client-group/client-user.dto";
import { GrantProjectAccessRequest } from "../models/client-group/grant-project-access-request.dto";
import { GrantProjectAccessResult } from "../models/client-group/grant-project-access-result.dto";
import { GroupProjectAssociationDto } from "../models/client-group/group-project-association.dto";
import { ProjectAccessListDto } from "../models/client-group/project-access-list.dto";
import { AccessApprovalDto } from "../models/client-group/access-approval.dto";
import { GroupUpsertResult } from "../models/client-group/group-upsert-result.dto";

/**
 * Service for managing client groups via the Sales Portal proxy API.
 * All calls are proxied through JNI.Portal.Core.V2 → Nadel.Client.Portal.Core.
 */
@Injectable({ providedIn: "root" })
export class ClientGroupService {
  private readonly baseUrl = `${environment.baseUrl}/ClientGroup`;

  /** Page size used when fetching the full set of groups for a client (no pagination). Capped at the server's maximum allowed page size. */
  private static readonly ALL_RECORDS_PAGE_SIZE = 200;

  constructor(private _http: HttpClient) {}

  /**
   * Lists approval requests/history for administrators using server pagination.
   * @param skip Records to skip.
   * @param take Page size (server maximum 200).
   * @param filters Supported account, project, email, group, ID and status filters.
   * @param sort Server sort fields.
   * @returns Approval rows with the total matching record count.
   */
  listAccessApprovals(
    skip: number,
    take: number,
    filters: GenericFilterRequestDto[] = [],
    sort: GenericSortRequestDto[] = []
  ): Observable<GenericResponseDto<AccessApprovalDto[]>> {
    return this._http
      .post<
        GenericResponseDto<AccessApprovalDto[]>
      >(`${this.baseUrl}/access-approvals/list`, { skip, take, filters, sort })
      .pipe(
        map(response => {
          if (response.status !== "success") {
            throw new Error(
              response.message || "Unable to load access approvals."
            );
          }
          return response;
        })
      );
  }

  /**
   * Decides a pending request and immediately dispatches approved invitations in the backend.
   * @param id Approval identifier.
   * @param decision Administrator decision.
   * @returns True only when the boolean success envelope confirms the decision.
   */
  decideAccessApproval(
    id: number,
    decision: "approve" | "reject"
  ): Observable<boolean> {
    return this._http
      .post<
        GenericResponseDto<boolean>
      >(`${this.baseUrl}/access-approvals/${id}/${decision}`, {})
      .pipe(
        map(response => {
          if (response.status !== "success" || response.data !== true) {
            throw new Error(
              response.message || "Unable to update access approval."
            );
          }
          return response.data;
        }),
        catchError((error: unknown) => {
          if (!(error instanceof HttpErrorResponse)) {
            return throwError(() => error);
          }

          const problem: unknown = error.error;
          const fallback =
            "Unable to complete the access request. Check its current status before retrying.";
          if (problem === null || typeof problem !== "object") {
            return throwError(() => new Error(fallback));
          }

          const message =
            "detail" in problem && typeof problem.detail === "string"
              ? problem.detail
              : "message" in problem && typeof problem.message === "string"
                ? problem.message
                : fallback;
          const traceId =
            "traceId" in problem && typeof problem.traceId === "string"
              ? problem.traceId
              : "proxyTraceId" in problem &&
                  typeof problem.proxyTraceId === "string"
                ? problem.proxyTraceId
                : null;
          return throwError(
            () =>
              new Error(
                traceId ? `${message} (Reference: ${traceId})` : message
              )
          );
        })
      );
  }

  /**
   * Retrieves a paginated list of clients with their configured group counts.
   * @param skip Number of records to skip.
   * @param take Number of records per page.
   * @param filters Column-level filters.
   * @param sort Sort specifications.
   * @returns Paginated response of client group summaries.
   */
  getClientsWithGroupCounts(
    skip: number,
    take: number,
    filters: GenericFilterRequestDto[] = [],
    sort: GenericSortRequestDto[] = []
  ): Observable<GenericResponseDto<ClientGroupSummaryDto[]>> {
    return this._http.post<GenericResponseDto<ClientGroupSummaryDto[]>>(
      `${this.baseUrl}/clients`,
      { skip, take, filters, sort }
    );
  }

  /**
   * Enables or disables a client's Client Portal access. When disabled, the project details
   * Client Access section becomes read-only and the order-line Proof column is hidden.
   * @param clientId The customer account identifier.
   * @param isEnabled Whether the client's Client Portal access should be enabled.
   * @returns The persisted enabled flag.
   */
  updateClientPortalAccess(
    clientId: string,
    isEnabled: boolean
  ): Observable<boolean> {
    return this._http
      .put<
        GenericResponseDto<boolean>
      >(`${this.baseUrl}/clients/${encodeURIComponent(clientId)}/portal-access`, { isEnabled })
      .pipe(map(response => response.data ?? isEnabled));
  }

  /**
   * Gets whether a client's Client Portal access is enabled. Clients without an explicit
   * record are treated as enabled.
   * @param clientId The customer account identifier.
   * @returns The client's enabled flag.
   */
  getClientPortalAccess(clientId: string): Observable<boolean> {
    return this._http
      .get<
        GenericResponseDto<boolean>
      >(`${this.baseUrl}/clients/${encodeURIComponent(clientId)}/portal-access`)
      .pipe(map(response => response.data ?? true));
  }

  /**
   * Retrieves a paginated list of groups for a specific client.
   * @param clientId The client account identifier.
   * @param skip Number of records to skip.
   * @param take Number of records per page.
   * @param filters Column-level filters.
   * @param sort Sort specifications.
   * @returns Paginated response of groups belonging to the client.
   */
  getGroupsByClient(
    clientId: string,
    skip: number,
    take: number,
    filters: GenericFilterRequestDto[] = [],
    sort: GenericSortRequestDto[] = []
  ): Observable<GenericResponseDto<ClientGroupDetailDto[]>> {
    return this._http.post<GenericResponseDto<ClientGroupDetailDto[]>>(
      `${this.baseUrl}/clients/${encodeURIComponent(clientId)}/groups/list`,
      { skip, take, filters, sort }
    );
  }

  /**
   * Retrieves the existing Nadel Portal users for a client, used to populate
   * the "Assign Users" autocomplete in the Create/Update Group dialogs.
   * @param customerAccount The customer account identifier.
   * @returns The list of client users available for assignment.
   */
  getClientUsers(customerAccount: string): Observable<ClientUserDto[]> {
    return this._http
      .get<
        GenericResponseDto<ClientUserDto[]>
      >(`${this.baseUrl}/clients/${encodeURIComponent(customerAccount)}/users`)
      .pipe(map(response => response.data ?? []));
  }

  /**
   * Searches for existing Nadel Portal users by email prefix. Used for autocomplete
   * in the "Add Person" field within the Add Group popup.
   * @param clientId The client account identifier.
   * @param email The search term (partial email or name).
   * @returns A filtered list of matching client users.
   */
  searchClientUsers(
    clientId: string,
    email: string
  ): Observable<ClientUserDto[]> {
    return this._http
      .get<
        GenericResponseDto<ClientUserDto[]>
      >(`${this.baseUrl}/clients/${encodeURIComponent(clientId)}/users/search`, { params: { email } })
      .pipe(map(response => response.data ?? []));
  }

  /**
   * Validates whether an email may be assigned to the given client under the single-client rule:
   * a user (by email) may belong to multiple groups, but only within one client. Completes when the
   * email is available; emits an error carrying the backend message when the email already belongs
   * to a different client so callers can surface it as an error notification.
   * @param clientId The client account identifier the email is being assigned to.
   * @param email The candidate email to validate.
   * @returns An observable that completes when the email is available for this client.
   */
  validateUserEmail(clientId: string, email: string): Observable<void> {
    return this._http
      .get<
        GenericResponseDto<boolean>
      >(`${this.baseUrl}/clients/${encodeURIComponent(clientId)}/users/validate-email`, { params: { email } })
      .pipe(
        map(response => {
          if (response.status !== "success" || response.data !== true) {
            throw new Error(
              response.message ||
                "This email is already associated with another client."
            );
          }
        })
      );
  }

  /**
   * Retrieves all enabled groups for a client without pagination, including their assigned users.
   * Used to populate the "Add Group" popup on the Project Access page; disabled groups are excluded
   * via a server-side filter on the enabled flag.
   * @param clientId The client account identifier.
   * @returns The list of enabled groups belonging to the client.
   */
  getAllGroupsByClient(clientId: string): Observable<ClientGroupDetailDto[]> {
    return this._http
      .post<GenericResponseDto<ClientGroupDetailDto[]>>(
        `${this.baseUrl}/clients/${encodeURIComponent(clientId)}/groups/list`,
        {
          skip: 0,
          take: ClientGroupService.ALL_RECORDS_PAGE_SIZE,
          sort: [],
        }
      )
      .pipe(map(response => response.data ?? []));
  }

  /**
   * Retrieves a single group by its identifier, including its assigned users.
   * Used to pre-populate the Update Group dialog.
   * @param groupId The group identifier.
   * @returns The group detail with its assigned users.
   */
  getGroupById(groupId: number): Observable<ClientGroupDetailDto> {
    return this._http
      .get<
        GenericResponseDto<ClientGroupDetailDto>
      >(`${this.baseUrl}/groups/${groupId}`)
      .pipe(map(response => response.data));
  }

  /**
   * Updates the enabled/disabled status of a group.
   * @param clientId The client account identifier.
   * @param groupId The group identifier.
   * @param isActive `true` to enable the group; otherwise, `false`.
   * @returns An observable that completes when the status is updated.
   */
  updateGroupStatus(
    clientId: string,
    groupId: number,
    isActive: boolean
  ): Observable<void> {
    return this._http.patch<void>(
      `${this.baseUrl}/clients/${encodeURIComponent(clientId)}/groups/${groupId}/status`,
      { isActive }
    );
  }

  /**
   * Checks whether a group is currently associated with one or more projects.
   * Called before disabling a group so the caller can warn the user that disabling
   * will delink the group from its projects.
   * @param groupId The group identifier.
   * @returns The association state and the number of distinct linked projects.
   */
  getGroupProjectAssociation(
    groupId: number
  ): Observable<GroupProjectAssociationDto> {
    return this._http
      .get<
        GenericResponseDto<GroupProjectAssociationDto>
      >(`${this.baseUrl}/groups/${groupId}/project-association`)
      .pipe(map(response => response.data));
  }

  /**
   * Resends the Nadel Portal invitation email to an assigned user.
   * Calls the new `POST /api/users/{email}/resend-invitation` endpoint.
   * @param email The email address of the user to resend the invitation to.
   * @returns An observable that completes when the resend has been triggered.
   */
  resendInvitation(email: string): Observable<void> {
    return this._http.post<void>(
      `${this.baseUrl}/users/${encodeURIComponent(email)}/resend-invitation`,
      {}
    );
  }

  /**
   * Creates a new group or updates an existing one for a client.
   * When `groupId` is provided the existing group is updated; otherwise a new group is created.
   * Emits an error carrying the backend message when the operation is rejected (e.g. a duplicate
   * group name), so callers can surface a meaningful validation message.
   * @param clientId The client account identifier.
   * @param payload The group create/update payload. Each user carries an `isActive` flag:
   * `true` adds the user to the group, `false` removes the user's active membership. Users
   * omitted from the payload are left untouched.
   * @returns The saved group's identifier along with any members held for administrator approval.
   */
  createOrUpdateGroup(
    clientId: string,
    payload: {
      groupId?: number | null;
      projectId?: string | null;
      groupName: string;
      description: string | null;
      users: { email: string; isActive?: boolean }[];
    }
  ): Observable<GroupUpsertResult> {
    return this._http
      .post<GenericResponseDto<GroupUpsertResult>>(
        `${this.baseUrl}/clients/${encodeURIComponent(clientId)}/groups`,
        {
          groupId: payload.groupId ?? null,
          projectId: payload.projectId ?? null,
          groupName: payload.groupName,
          description: payload.description,
          users: payload.users.map(user => ({
            email: user.email,
            isActive: user.isActive ?? true,
          })),
        }
      )
      .pipe(
        map(response => {
          // The backend returns HTTP 200 with status "error" for business-rule rejections
          // (such as a duplicate group name); convert those into an error notification so the
          // caller's error handler can display the backend-provided message.
          if (response.status !== "success" || !response.data) {
            throw new Error(response.message || "Unable to save group.");
          }
          return response.data;
        })
      );
  }

  /**
   * Grants the project buyer portal access through the sales-portal proxy.
   * This resolves or creates the buyer in the Nadel Portal and reactivates any disabled profile.
   * @param projectId The project (workflow) identifier.
   * @param payload The buyer access request.
   * @returns The portal access grant result.
   */
  grantPrimaryBuyerAccess(
    projectId: number,
    payload: {
      buyerEmail: string;
      buyerName: string;
      clientId: string;
      projectName?: string;
      personalMessage?: string | null;
    }
  ): Observable<GrantProjectAccessResult> {
    return this._http.post<GrantProjectAccessResult>(
      `${this.baseUrl}/projects/${projectId}/primary-buyer`,
      payload
    );
  }

  /**
   * Retrieves the current active project access, organised into groups (with their granted
   * users) and the individual (additional) users that have direct access.
   * @param projectId The project (workflow) identifier.
   * @returns The project's grouped and individual access listing.
   */
  getProjectAccess(projectId: number): Observable<ProjectAccessListDto> {
    return this._http
      .get<
        GenericResponseDto<ProjectAccessListDto>
      >(`${this.baseUrl}/projects/${projectId}/access`)
      .pipe(
        map(response => {
          if (response.status !== "success" || !response.data) {
            throw new Error(
              response.message || "Unable to load project access."
            );
          }
          return response.data;
        })
      );
  }

  /**
   * Synchronises project access to match the supplied desired state. Newly added users are
   * granted access and emailed an invitation; entries no longer present are removed.
   * @param projectId The project (workflow) identifier.
   * @param payload The complete desired project access state.
   * @returns The synchronisation result containing counts of users added, invited, and removed.
   */
  grantProjectAccess(
    projectId: number,
    payload: GrantProjectAccessRequest
  ): Observable<GrantProjectAccessResult> {
    return this._http.post<GrantProjectAccessResult>(
      `${this.baseUrl}/projects/${projectId}/access`,
      payload
    );
  }

  /**
   * Resends the project invitation email to a single user who already has active access to the
   * project. Used to retry a failed invitation; the backend updates the invitation log with the
   * retry outcome (delivered or failure reason).
   * @param projectId The project (workflow) identifier.
   * @param userId The identifier of the user to resend the project invitation to.
   * @returns An observable that completes when the resend has been triggered.
   */
  resendProjectAccessInvitation(
    projectId: number,
    userId: number
  ): Observable<boolean> {
    return this._http
      .post<
        GenericResponseDto<boolean>
      >(`${this.baseUrl}/projects/${projectId}/access/${userId}/resend`, {})
      .pipe(
        map(response => {
          if (response.status !== "success" || response.data !== true) {
            throw new Error(
              response.message || "Unable to resend project invitation."
            );
          }
          return response.data;
        })
      );
  }
}
